import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress, Alert, Stack,
  Grid, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Card, CardContent, Tooltip, TextField
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CloseIcon from '@mui/icons-material/Close';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import axios from 'axios';
import { toast } from 'react-toastify';

const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];

const PresentielReservationsTable = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Date sélectionnée (par défaut la date actuelle du jour)
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Modale de détails & validation
  const [selectedRes, setSelectedRes] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  // Référence pour le défilement horizontal
  const scrollContainerRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
  const token = localStorage.getItem('token');

  // 🗓️ Générer dynamiquement 1 semaine avant (J-7) et 1 semaine après (J+7) autour de selectedDate
  const twoWeeksRange = useMemo(() => {
    const days = [];
    const baseDate = new Date(selectedDate);
    
    // -7 jours jusqu'à +7 jours
    for (let i = -7; i <= 7; i++) {
      const d = new Date(baseDate);
      d.setDate(baseDate.getDate() + i);
      days.push(d.toISOString().split('T')[0]);
    }
    return days;
  }, [selectedDate]);

  const fetchPresentielReservations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/admin/reservations/presentiel`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      const data = response.data.result || response.data.data || [];
      setReservations(data);
      setError(null);
    } catch (err) {
      console.error('Erreur chargement réservations présentiel :', err);
      setError('Erreur lors du chargement des réservations médicales en présentiel.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPresentielReservations();
  }, []);

  // Décalage de 7 jours (1 semaine précédente / 1 semaine suivante)
  const handleShiftWeek = (offsetDays) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offsetDays);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  // Naviguer directement au jour présent (Aujourd'hui)
  const handleGoToday = () => {
    const todayStr = new Date().toISOString().split('T')[0];
    setSelectedDate(todayStr);
  };

  // Filtrer les réservations du jour sélectionné
  const dayReservations = useMemo(() => {
    return reservations.filter(r => r.start_time_reservation.startsWith(selectedDate));
  }, [reservations, selectedDate]);

  // Demandes en attente de validation
  const pendingReservations = useMemo(() => {
    return reservations.filter(r => r.is_validated === 'en_attente');
  }, [reservations]);

  // Compteurs par jour
  const getDayStats = (dateStr) => {
    const dayRes = reservations.filter(r => r.start_time_reservation.startsWith(dateStr));
    return {
      total: dayRes.length,
      validated: dayRes.filter(r => r.is_validated === 'valide').length,
      pending: dayRes.filter(r => r.is_validated === 'en_attente').length,
    };
  };

  const handleValidate = async (id) => {
    try {
      await axios.post(`${apiUrl}/admin/reservations/${id}/valider`, {}, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
      });
      toast.success('Consultation/Soin validé avec succès.');
      fetchPresentielReservations();
      setOpenModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la validation.');
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.post(`${apiUrl}/admin/reservations/${id}/refuser`, {}, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
      });
      toast.success('Consultation/Soin refusé.');
      fetchPresentielReservations();
      setOpenModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors du refus.');
    }
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateFr = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const formatDateFull = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <Box sx={{ mt: 1 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* 🏥 BARRE SUPÉRIEURE : DEMANDES EN ATTENTE DE VALIDATION */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: '16px', border: '1px solid #FFE58F', bgcolor: '#FFFBE6' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningAmberIcon color="warning" />
            <Typography variant="h6" fontWeight="800" color="#8C6B00">
              Demandes de rendez-vous à valider ({pendingReservations.length})
            </Typography>
          </Stack>
          <Chip label="Locaux SOA" color="warning" size="small" sx={{ fontWeight: 700 }} />
        </Stack>

        {loading ? (
          <CircularProgress size={24} />
        ) : pendingReservations.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Toutes les demandes de consultation en présentiel ont été traitées.
          </Typography>
        ) : (
          <Stack direction="row" spacing={2} sx={{ overflowX: 'auto', pb: 1 }}>
            {pendingReservations.map((res) => {
              const resDate = res.start_time_reservation.split('T')[0];

              return (
                <Card
                  key={res.id_offre_reservation}
                  elevation={0}
                  onClick={() => setSelectedDate(resDate)}
                  sx={{
                    minWidth: '290px',
                    borderRadius: '12px',
                    border: selectedDate === resDate ? '2px solid #ed6c02' : '1px solid #E9ECEF',
                    bgcolor: '#FFFFFF',
                    cursor: 'pointer',
                    '&:hover': { boxShadow: '0px 4px 12px rgba(0,0,0,0.08)' }
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2" fontWeight="bold" color="#2B3674">
                        Patient : {res.client?.nom_client} {res.client?.prenom_client}
                      </Typography>
                      <Chip label={`${res.prix_reservation} €`} size="small" color="primary" sx={{ fontWeight: 700, height: 20 }} />
                    </Stack>

                    <Typography variant="body2" color="primary.main" fontWeight="700" sx={{ mt: 0.5 }}>
                      {res.offre?.info_offre?.titre_offre}
                    </Typography>

                    <Typography variant="caption" color="text.secondary" display="block">
                      Praticien : {res.user?.nom_user} {res.user?.prenom_user}
                    </Typography>

                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ my: 1, color: '#ed6c02' }}>
                      <AccessTimeIcon fontSize="small" />
                      <Typography variant="caption" fontWeight="bold">
                        {formatDateFr(resDate)} à {formatTime(res.start_time_reservation)}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        fullWidth
                        startIcon={<CheckCircleIcon />}
                        onClick={(e) => { e.stopPropagation(); handleValidate(res.id_offre_reservation); }}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '11px' }}
                      >
                        Valider
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        fullWidth
                        startIcon={<CancelIcon />}
                        onClick={(e) => { e.stopPropagation(); handleReject(res.id_offre_reservation); }}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '11px' }}
                      >
                        Refuser
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </Paper>

      {/* 🗓️ SÉLECTEUR 1 SEMAINE AVANT / 1 SEMAINE APRÈS */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '16px', border: '1px solid #eceef0', bgcolor: '#F4F7FE' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarMonthIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="800" color="#2B3674">
              Aperçu (1 Semaine avant & 1 Semaine après)
            </Typography>
          </Stack>

          {/* Navigation : Semaine Précédente / Suivante + DatePicker + Aujourd'hui */}
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton size="small" onClick={() => handleShiftWeek(-7)} sx={{ bgcolor: '#FFFFFF', border: '1px solid #E9ECEF' }}>
              <Tooltip title="Semaine précédente"><ChevronLeftIcon /></Tooltip>
            </IconButton>

            <TextField
              type="date"
              size="small"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              sx={{ bgcolor: '#FFFFFF', borderRadius: '8px', '& input': { p: '6px 10px', fontSize: '13px', fontWeight: 700 } }}
            />

            <Button
              variant="contained"
              size="small"
              onClick={handleGoToday}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none' }}
            >
              Aujourd'hui
            </Button>

            <IconButton size="small" onClick={() => handleShiftWeek(7)} sx={{ bgcolor: '#FFFFFF', border: '1px solid #E9ECEF' }}>
              <Tooltip title="Semaine suivante"><ChevronRightIcon /></Tooltip>
            </IconButton>
          </Stack>
        </Stack>

        {/* Grille glissante des 15 jours (-7j à +7j) */}
        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            gap: 1.2,
            overflowX: 'auto',
            pb: 1,
            pt: 0.5,
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': { height: '6px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' }
          }}
        >
          {twoWeeksRange.map((dateStr) => {
            const isSelected = selectedDate === dateStr;
            const isToday = dateStr === new Date().toISOString().split('T')[0];
            const stats = getDayStats(dateStr);

            return (
              <Box
                key={dateStr}
                onClick={() => setSelectedDate(dateStr)}
                sx={{
                  minWidth: '92px',
                  p: 1.2,
                  borderRadius: '12px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: '0.2s',
                  bgcolor: isSelected ? '#2B3674' : isToday ? '#E3F2FD' : '#FFFFFF',
                  color: isSelected ? '#FFFFFF' : '#2B3674',
                  border: isSelected ? '2px solid #2B3674' : isToday ? '2px solid #1976D2' : '1px solid #E9ECEF',
                  '&:hover': { bgcolor: isSelected ? '#2B3674' : '#EAEFFC' }
                }}
              >
                <Typography variant="caption" sx={{ textTransform: 'uppercase', opacity: 0.8, display: 'block', fontWeight: 700 }}>
                  {formatDateFr(dateStr).split(' ')[0]}
                </Typography>
                <Typography variant="body1" fontWeight="800" sx={{ my: 0.2 }}>
                  {formatDateFr(dateStr).split(' ')[1]} {formatDateFr(dateStr).split(' ')[2]}
                </Typography>

                <Stack direction="row" justifyContent="center" spacing={0.5} sx={{ mt: 0.8 }}>
                  {stats.validated > 0 && (
                    <Tooltip title={`${stats.validated} rendez-vous validé(s)`}>
                      <Chip
                        label={`${stats.validated} ✓`}
                        size="small"
                        color="success"
                        sx={{ height: 18, fontSize: '10px', fontWeight: 700 }}
                      />
                    </Tooltip>
                  )}
                  {stats.pending > 0 && (
                    <Tooltip title={`${stats.pending} demande(s) en attente`}>
                      <Chip
                        label={`${stats.pending} ?`}
                        size="small"
                        color="warning"
                        sx={{ height: 18, fontSize: '10px', fontWeight: 700 }}
                      />
                    </Tooltip>
                  )}
                  {stats.total === 0 && (
                    <Typography variant="caption" sx={{ fontSize: '10px', opacity: 0.5 }}>
                      Libre
                    </Typography>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* 🩺 GRILLE HORAIRE MÉDICALE DU JOUR SÉLECTIONNÉ */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: '16px', border: '1px solid #eceef0', bgcolor: '#FFFFFF' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight="bold" color="#2B3674" sx={{ textTransform: 'capitalize' }}>
            Planning Médical : {formatDateFull(selectedDate)}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip icon={<EventAvailableIcon />} label="🟢 Validé" size="small" variant="outlined" color="success" />
            <Chip icon={<WarningAmberIcon />} label="🟠 En attente" size="small" variant="outlined" color="warning" />
          </Stack>
        </Stack>

        <Box sx={{ borderTop: '1px solid #E9ECEF' }}>
          {HOURS.map((hour) => {
            const hourSlotReservations = dayReservations.filter(r => {
              const h = new Date(r.start_time_reservation).getHours();
              return h === hour;
            });

            return (
              <Grid container key={hour} sx={{ minHeight: '75px', borderBottom: '1px solid #F1F3F5' }}>
                <Grid item xs={2} sm={1} sx={{ py: 1.5, pr: 2, borderRight: '1px dashed #E9ECEF', color: '#2B3674', fontWeight: 800 }}>
                  {`${hour}:00`}
                </Grid>

                <Grid item xs={10} sm={11} sx={{ p: 1, bgcolor: hourSlotReservations.length > 1 ? '#FFFDF5' : 'transparent' }}>
                  {hourSlotReservations.length === 0 ? (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                      <Typography variant="caption" color="grey.400" sx={{ fontStyle: 'italic' }}>
                        Salle de consultation disponible
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={1}>
                      {hourSlotReservations.map((res) => {
                        const isValidated = res.is_validated === 'valide';
                        const isPending = res.is_validated === 'en_attente';

                        return (
                          <Grid item xs={12} md={hourSlotReservations.length > 1 ? 6 : 12} key={res.id_offre_reservation}>
                            <Paper
                              elevation={0}
                              onClick={() => { setSelectedRes(res); setOpenModal(true); }}
                              sx={{
                                p: 1.5,
                                borderRadius: '10px',
                                cursor: 'pointer',
                                bgcolor: isValidated ? '#E6F4EA' : isPending ? '#FFF4E5' : '#FCE8E6',
                                borderLeft: `5px solid ${isValidated ? '#34A853' : isPending ? '#FB8C00' : '#EA4335'}`,
                                transition: '0.2s',
                                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0px 4px 8px rgba(0,0,0,0.06)' }
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" fontWeight="bold" color="#2B3674">
                                  {formatTime(res.start_time_reservation)} - {formatTime(res.end_time_reservation)} | {res.offre?.info_offre?.titre_offre}
                                </Typography>
                                <Chip
                                  label={isValidated ? 'Validé' : isPending ? 'En attente' : 'Refusé'}
                                  size="small"
                                  color={isValidated ? 'success' : isPending ? 'warning' : 'error'}
                                  sx={{ fontWeight: 700, fontSize: '10px', height: '20px' }}
                                />
                              </Stack>

                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Patient: <b>{res.client?.nom_client} {res.client?.prenom_client}</b> | Praticien: <b>{res.user?.nom_user} ({res.user?.title_user || 'Praticien'})</b>
                              </Typography>
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            );
          })}
        </Box>
      </Paper>

      {/* 🔴 MODAL MÉDICAL D'ANALYSE ET VALIDATION */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ p: 2, bgcolor: '#F4F7FE', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold" color="#2B3674">
            Fiche Consultation / Réservation #{selectedRes?.id_offre_reservation}
          </Typography>
          <IconButton onClick={() => setOpenModal(false)}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          {selectedRes && (
            <Stack spacing={2}>
              <Box sx={{ p: 2, bgcolor: '#F8F9FA', borderRadius: '12px', border: '1px solid #E9ECEF' }}>
                <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MedicalServicesIcon fontSize="small" /> Soin & Prestation
                </Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                  {selectedRes.offre?.info_offre?.titre_offre}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Horaire : {formatDateFull(selectedRes.start_time_reservation)} ({formatTime(selectedRes.start_time_reservation)} - {formatTime(selectedRes.end_time_reservation)})
                </Typography>
                <Typography variant="body2" color="success.main" fontWeight="bold" sx={{ mt: 0.5 }}>
                  Tarif : {selectedRes.prix_reservation} €
                </Typography>
              </Box>

              <Box sx={{ p: 2, bgcolor: '#F8F9FA', borderRadius: '12px', border: '1px solid #E9ECEF' }}>
                <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" /> Information Patient
                </Typography>
                <Typography variant="body2"><b>Nom & Prénom :</b> {selectedRes.client?.nom_client} {selectedRes.client?.prenom_client}</Typography>
                <Typography variant="body2"><b>Email :</b> {selectedRes.client?.email_client}</Typography>
                <Typography variant="body2"><b>Téléphone :</b> {selectedRes.client?.phone_client || 'N/A'}</Typography>
              </Box>

              <Box sx={{ p: 2, bgcolor: '#F8F9FA', borderRadius: '12px', border: '1px solid #E9ECEF' }}>
                <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalHospitalIcon fontSize="small" /> Praticien Référent
                </Typography>
                <Typography variant="body2"><b>Praticien :</b> {selectedRes.user?.nom_user} {selectedRes.user?.prenom_user}</Typography>
                <Typography variant="body2"><b>Spécialité :</b> {selectedRes.user?.title_user || 'Général'}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: '#F4F7FE' }}>
          {selectedRes?.is_validated === 'en_attente' && (
            <>
              <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={() => handleValidate(selectedRes.id_offre_reservation)}>
                Valider la consultation
              </Button>
              <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => handleReject(selectedRes.id_offre_reservation)}>
                Refuser
              </Button>
            </>
          )}
          <Button onClick={() => setOpenModal(false)} color="inherit">Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PresentielReservationsTable;